/**
 * DuelInsights — Pro-gated analytics for duel performance.
 *
 * Shows win rate trend, most successful lift, average margin,
 * opponent analysis, and best time of day.
 */

import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';
import { useRouter } from 'expo-router';
import { Crown, TrendingUp, Target, Clock, Users } from 'lucide-react-native';

interface OpponentRecord {
  name: string;
  wins: number;
  losses: number;
}

interface InsightsData {
  winRateLast30: number;
  totalDuels30: number;
  bestLift: string | null;
  avgMargin: number;
  bestHour: number | null;
  opponents: OpponentRecord[];
}

export default function DuelInsights() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const router = useRouter();
  const c = theme.colors;
  const [data, setData] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);

  const isPro = hasAtLeast(tier, 'pro');

  useEffect(() => {
    if (!profile?.id || !isPro) {
      setLoading(false);
      return;
    }
    loadInsights();
  }, [profile?.id, isPro]);

  const loadInsights = async () => {
    if (!profile?.id) return;
    try {
      const cutoff = new Date(Date.now() - 30 * 86400000).toISOString();

      const { data: duels } = await supabase
        .from('duels')
        .select('id, status, winner_user_id, challenger_id, challenged_id, challenger_score, challenged_score, lift_type, start_at, created_at, challenger:profiles!duels_challenger_id_fkey(display_name,handle), opponent:profiles!duels_opponent_id_fkey(display_name,handle)')
        .or(`challenger_id.eq.${profile.id},challenged_id.eq.${profile.id}`)
        .eq('status', 'completed')
        .gte('created_at', cutoff);

      if (!duels || duels.length === 0) {
        setData({ winRateLast30: 0, totalDuels30: 0, bestLift: null, avgMargin: 0, bestHour: null, opponents: [] });
        setLoading(false);
        return;
      }

      const wins = duels.filter((d) => d.winner_user_id === profile.id);
      const winRate = duels.length > 0 ? wins.length / duels.length : 0;

      // Best lift type
      const liftWins: Record<string, number> = {};
      wins.forEach((d) => {
        if (d.lift_type) liftWins[d.lift_type] = (liftWins[d.lift_type] || 0) + 1;
      });
      const bestLift = Object.entries(liftWins).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

      // Average victory margin
      const margins = wins.map((d) => {
        const myScore = d.challenger_id === profile.id ? d.challenger_score : d.challenged_score;
        const oppScore = d.challenger_id === profile.id ? d.challenged_score : d.challenger_score;
        return (myScore ?? 0) - (oppScore ?? 0);
      }).filter((m) => m > 0);
      const avgMargin = margins.length > 0 ? margins.reduce((a, b) => a + b, 0) / margins.length : 0;

      // Best hour
      const hourWins: Record<number, number> = {};
      wins.forEach((d) => {
        const h = new Date(d.start_at || d.created_at).getHours();
        hourWins[h] = (hourWins[h] || 0) + 1;
      });
      const bestHour = Object.entries(hourWins).sort((a, b) => b[1] - a[1])[0]
        ? parseInt(Object.entries(hourWins).sort((a, b) => b[1] - a[1])[0][0])
        : null;

      // Opponent analysis
      const oppMap: Record<string, { name: string; wins: number; losses: number }> = {};
      duels.forEach((d) => {
        const isChallenger = d.challenger_id === profile.id;
        const oppId = isChallenger ? d.challenged_id : d.challenger_id;
        const oppProfile = isChallenger ? (d.opponent as any) : (d.challenger as any);
        const oppName = oppProfile?.display_name || oppProfile?.handle || 'Unknown';
        if (!oppId) return;
        if (!oppMap[oppId]) oppMap[oppId] = { name: oppName, wins: 0, losses: 0 };
        if (d.winner_user_id === profile.id) oppMap[oppId].wins++;
        else if (d.winner_user_id) oppMap[oppId].losses++;
      });

      setData({
        winRateLast30: winRate,
        totalDuels30: duels.length,
        bestLift,
        avgMargin: Math.round(avgMargin),
        bestHour,
        opponents: Object.values(oppMap).sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses)).slice(0, 5),
      });
    } catch (err) {
      console.warn('[DuelInsights] Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  // Free users see upgrade prompt
  if (!isPro) {
    return (
      <TouchableOpacity
        style={[styles.card, { backgroundColor: c.surface, borderColor: c.accent }]}
        onPress={() => router.push('/paywall')}
        activeOpacity={0.7}
      >
        <View style={styles.headerRow}>
          <Crown size={20} color={c.accent} />
          <Text style={[styles.title, { color: c.text }]}>📊 Duel Insights</Text>
        </View>
        <Text style={[styles.upgradeText, { color: c.textSecondary }]}>
          Unlock win patterns, opponent analysis, and performance trends with Pro.
        </Text>
        <Text style={[styles.upgradeLink, { color: c.accent }]}>Upgrade to Pro →</Text>
      </TouchableOpacity>
    );
  }

  if (loading || !data) {
    return (
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[styles.title, { color: c.textSecondary }]}>Loading insights...</Text>
      </View>
    );
  }

  const formatHour = (h: number) => {
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  return (
    <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.headerRow}>
        <Text style={[styles.title, { color: c.text }]}>📊 Duel Insights</Text>
        <Text style={[styles.period, { color: c.textTertiary }]}>Last 30 days</Text>
      </View>

      {data.totalDuels30 === 0 ? (
        <Text style={[styles.emptyText, { color: c.textSecondary }]}>
          No completed duels in the last 30 days. Start dueling to see insights!
        </Text>
      ) : (
        <View style={styles.grid}>
          <View style={styles.statRow}>
            <TrendingUp size={16} color={c.accent} />
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>Win Rate</Text>
            <Text style={[styles.statValue, { color: c.text }]}>{Math.round(data.winRateLast30 * 100)}% ({data.totalDuels30} duels)</Text>
          </View>

          {data.bestLift && (
            <View style={styles.statRow}>
              <Target size={16} color={c.accent} />
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Best Lift</Text>
              <Text style={[styles.statValue, { color: c.text }]}>{data.bestLift}</Text>
            </View>
          )}

          {data.avgMargin > 0 && (
            <View style={styles.statRow}>
              <TrendingUp size={16} color={c.accent} />
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Avg Margin</Text>
              <Text style={[styles.statValue, { color: c.text }]}>+{data.avgMargin} lbs</Text>
            </View>
          )}

          {data.bestHour !== null && (
            <View style={styles.statRow}>
              <Clock size={16} color={c.accent} />
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Best Time</Text>
              <Text style={[styles.statValue, { color: c.text }]}>{formatHour(data.bestHour)}</Text>
            </View>
          )}

          {data.opponents.length > 0 && (
            <View style={styles.oppSection}>
              <View style={[styles.statRow, { marginBottom: 4 }]}>
                <Users size={16} color={c.accent} />
                <Text style={[styles.statLabel, { color: c.textSecondary }]}>Opponents</Text>
              </View>
              {data.opponents.map((opp) => (
                <View key={opp.name} style={styles.oppRow}>
                  <Text style={[styles.oppName, { color: c.text }]}>{opp.name}</Text>
                  <Text style={[styles.oppRecord, { color: c.textSecondary }]}>
                    {opp.wins}W - {opp.losses}L
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 8,
  },
  title: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
    flex: 1,
  },
  period: {
    fontSize: 11,
    fontFamily: TYPO.body.regular,
  },
  upgradeText: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    lineHeight: 18,
    marginBottom: 8,
  },
  upgradeLink: {
    fontSize: 14,
    fontFamily: TYPO.body.bold,
  },
  emptyText: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    lineHeight: 18,
  },
  grid: {
    gap: 10,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statLabel: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    width: 80,
  },
  statValue: {
    fontSize: 14,
    fontFamily: TYPO.body.bold,
    flex: 1,
  },
  oppSection: {
    marginTop: 4,
    gap: 4,
  },
  oppRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 24,
    paddingVertical: 2,
  },
  oppName: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
  },
  oppRecord: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
});
